import { LoadSpinner } from '@calizahq/react-components-v2'
import { WorkspaceTab } from '@sales-app/context/workspace/workspace.type'
import { useFavoritesPinnedHomes } from '@sales-app/hooks/useFavoritesPinnedHomes/useFavoritesPinnedHomes'
import { RecommendedHomeRow } from '@sales-app/hooks/useFavoritesPinnedHomes/useFavoritesPinnedHomes.util'
import { useRecommendedHomes } from '@sales-app/hooks/useRecommendedHomes/useRecommendedHomes'
import { useRecommendedHomesMutations } from '@sales-app/hooks/useRecommendedHomesMutations/useRecommendedHomesMutations'
import { Table } from '@shared/components'
import React, { FC, useCallback, useMemo } from 'react'

import { RecomendedHomesColumns, transformHomes } from '../RecommendedHomes.data'
import { ListPagination } from './ListPagination'

type Props = {
  bookablePerson: WorkspaceTab
}

export const ListView: FC<Props> = ({ bookablePerson }) => {
  const { data, isLoading, isRefetching } = useRecommendedHomes()

  const { homes } = useFavoritesPinnedHomes()
  const activeFilters = useMemo(() => {
    return bookablePerson.potentialBookingFilters || { listView: true }
  }, [bookablePerson.potentialBookingFilters])
  const { potentialBookingId } = bookablePerson
  const { mutateCreatePin, mutateCreateFavorite, mutateRemoveFavorite, mutateRemovePin, isMutationLoading } =
    useRecommendedHomesMutations()

  const handlePinHomeClick = useCallback(
    (home: RecommendedHomeRow) => {
      const {
        id,
        pinnedId,
        pinned,
        name,
        square_footage = 0,
        bedrooms,
        potentialBookingId,
        market,
        baths,
        displayCityId,
      } = home
      if (potentialBookingId) {
        if (!pinned) {
          if (displayCityId) {
            mutateCreatePin({
              pinnedHome: {
                displayCityId,
                homeId: id,
                unitName: name,
                state: market,
                squareFootage: square_footage,
                bedrooms,
                baths,
                potentialBookingId,
              },
            })
          }
        } else {
          if (pinnedId) {
            mutateRemovePin({
              pinnedHomes: { ids: [pinnedId] },
            })
          }
        }
      }
    },
    [mutateCreatePin, mutateRemovePin],
  )

  const handlefavoriteHomeClick = useCallback(
    (home: RecommendedHomeRow) => {
      const { id, bookablePersonId, favoriteId, favorite } = home
      if (favorite) {
        if (favoriteId) {
          mutateRemoveFavorite({
            favoriteId: favoriteId,
            favorite: {
              userId: bookablePersonId,
            },
          })
        }
      } else {
        if (id && bookablePersonId) {
          mutateCreateFavorite({
            favorite: {
              homeId: id,
              userId: bookablePersonId,
            },
          })
        }
      }
    },
    [mutateCreateFavorite, mutateRemoveFavorite],
  )

  const formattedHomes = useMemo(() => {
    return data && data.data.homes ? transformHomes(data.data, potentialBookingId, homes, bookablePerson) : []
  }, [data, potentialBookingId, bookablePerson, homes])

  const columns = useMemo(() => {
    return RecomendedHomesColumns(activeFilters, isMutationLoading, handlePinHomeClick, handlefavoriteHomeClick)
  }, [activeFilters, handlePinHomeClick, handlefavoriteHomeClick, isMutationLoading])

  return (
    <div className="flex h-full flex-col bg-white" id="search-home-list-wrapper">
      {isLoading || isRefetching ? (
        <div className="flex h-20 w-full items-center justify-center">
          <LoadSpinner className="icon-secondary" isCurrentColor={true} size={2} />
        </div>
      ) : (
        <>
          <Table className="h-full" columns={columns as any} data={formattedHomes} />
          {!!data?.data.homes.length && (
            <ListPagination
              currentPage={data?.data.current_page}
              resultsPerPage={data?.data.results_per_page}
              totalPages={data?.data.total_pages}
              totalResults={data?.data.total_homes}
            />
          )}
        </>
      )}
    </div>
  )
}
